import { CPLError } from "cpl-react-common/Helpers/CustomErrors";
import {
  AddressModel,
  DirectorModel,
  NewDirectorModel,
  UpdateAddressModel,
  UpdateDirectorModel,
  ValidationProblemDetails,
} from "services/api/cplapi/CPLAPI";
import { AddressFormValues, emptyAddressFormValues } from "../../shared/address/schemas/addressSchema";
import { toNewAddressModel, toUpdateAddressModel } from "../../shared/address/utils/addressUtils";
import { CountryOption } from "../../shared/selectComponents/countrySelect/countryOptions";
import { DirectorFormValues } from "../schemas/directorSchema";

export const toNewDirectorModel = (director: DirectorFormValues): NewDirectorModel =>
  new NewDirectorModel({
    firstName: director.firstName.trim(),
    lastName: director.lastName.trim(),
    emailAddress: director.emailAddress.trim(),
    phoneNumber: director.phoneNumber.trim(),
    address: toNewAddressModel(director.address),
    legalEntityIDs: director.legalEntityIDs,
  });

export const toUpdateDirectorModel = (directorID: string, director: DirectorFormValues): UpdateDirectorModel =>
  new UpdateDirectorModel({
    directorID,
    firstName: director.firstName.trim(),
    lastName: director.lastName.trim(),
    emailAddress: director.emailAddress.trim(),
    phoneNumber: director.phoneNumber.trim(),
    legalEntityIDs: director.legalEntityIDs,
  });

const toAddressFormValues = (address: AddressModel | undefined, countries: CountryOption[]): AddressFormValues => {
  if (!address) {
    return emptyAddressFormValues;
  }

  // TODO(10430): countryCodeID is matched against the sortOrder stand-in (see countryOptions.ts) until CountryCodeModel has an ID
  const countryCodeID = address.countryCodeID;
  const countryIsoCode = countries.find((country) => country.countryCodeID === countryCodeID)?.isO3166_1_2LetterCode;

  return {
    addressLines: [
      address.addressLine1 ?? "",
      address.addressLine2 ?? "",
      address.addressLine3 ?? "",
      address.addressLine4 ?? "",
      address.addressLine5 ?? "",
    ],
    city: address.city ?? "",
    county: address.county ?? "",
    countryCodeID,
    countryIsoCode,
    postCode: address.postCode ?? "",
  };
};

export const toDirectorFormValues = (director: DirectorModel, countries: CountryOption[]): DirectorFormValues => ({
  firstName: director.firstName ?? "",
  lastName: director.lastName ?? "",
  emailAddress: director.emailAddress ?? "",
  phoneNumber: director.phoneNumber ?? "",
  address: toAddressFormValues(director.address, countries),
  legalEntityIDs: (director.linkedLegalEntities ?? [])
    .map((legalEntity) => legalEntity.legalEntityID)
    .filter((legalEntityID): legalEntityID is string => !!legalEntityID),
});

export type DirectorUpdate = { director?: UpdateDirectorModel; address?: UpdateAddressModel };

const isSameModel = (a: { toJSON(): object }, b: { toJSON(): object }) =>
  JSON.stringify(a.toJSON()) === JSON.stringify(b.toJSON());

export const toDirectorUpdate = (
  directorID: string,
  addressID: string,
  initialValues: DirectorFormValues,
  values: DirectorFormValues
): DirectorUpdate => {
  const director = toUpdateDirectorModel(directorID, values);
  const address = toUpdateAddressModel(addressID, values.address);

  return {
    director: isSameModel(director, toUpdateDirectorModel(directorID, initialValues)) ? undefined : director,
    address: isSameModel(address, toUpdateAddressModel(addressID, initialValues.address)) ? undefined : address,
  };
};

export type UpdateDirectorFailure = {
  part: "director" | "address";
  validationErrors: Record<string, string[]>;
};

// The address endpoint names its fields without the "Address." prefix the form's error mapping expects
const withAddressFieldKeys = (errors: Record<string, string[]> = {}) =>
  Object.fromEntries(Object.entries(errors).map(([key, messages]) => [`Address.${key}`, messages]));

export const getFailedDirectorParts = (
  directorResult: PromiseSettledResult<unknown> | undefined,
  addressResult: PromiseSettledResult<unknown> | undefined
): UpdateDirectorFailure[] => {
  const directorError = directorResult?.status === "rejected" ? directorResult.reason : undefined;
  const addressError = addressResult?.status === "rejected" ? addressResult.reason : undefined;

  const isNothingSaved = (!directorResult || directorError) && (!addressResult || addressError);
  const unexpectedError = [directorError, addressError].find(
    (error) => error instanceof CPLError || (error && isNothingSaved && !(error instanceof ValidationProblemDetails))
  );
  if (unexpectedError) throw unexpectedError;

  const failures: UpdateDirectorFailure[] = [];
  if (directorError) failures.push({ part: "director", validationErrors: directorError.errors ?? {} });
  if (addressError) failures.push({ part: "address", validationErrors: withAddressFieldKeys(addressError.errors) });
  return failures;
};
